"use client"

import * as React from "react"
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "lucide-react"
import { DayButton, DayPicker } from "react-day-picker"

import { cn } from "../../lib/utils"
import { Button, buttonVariants } from "../../components/ui/button"

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "ghost",
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>["variant"]
}) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        "bg-white dark:bg-black text-gray-900 dark:text-white group/calendar p-2 rounded-md shadow-md border border-gray-300 dark:border-gray-700",
        className
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString("default", { month: "short" }),
        ...formatters,
      }}
      classNames={{
        root: "w-[300px] ",
        months: "flex flex-col gap-4",
        month: "flex flex-col gap-2",
        nav: "flex items-center justify-between mb-2",
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          "p-1 rounded-full hover:bg-gray-100"
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          "p-1 rounded-full hover:bg-gray-100"
        ),
        month_caption: "text-base font-semibold text-center text-gray-900 dark:text-white",
        weekdays: "grid grid-cols-7 text-xs text-gray-600 dark:text-white",
        weekday: "text-center font-medium text-gray-700 dark:text-white",
        week: "grid grid-cols-7 gap-0.5",
        day: cn(
          "flex items-center justify-center p-1 rounded-md text-xs font-medium hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-900 dark:text-white",
          "data-[selected=true]:bg-blue-500 data-[selected=true]:text-white dark:data-[selected=true]:bg-blue-600"
        ),
        range_start: "rounded-l-md bg-blue-500 text-white dark:bg-blue-600",
        range_middle: "bg-blue-300 text-white dark:bg-blue-500/60",
        range_end: "rounded-r-md bg-blue-500 text-white dark:bg-blue-600",
        today: "bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-white font-semibold",
        outside: "text-gray-400 dark:text-white/40",
        disabled: "text-gray-300 dark:text-white/20",
        hidden: "hidden",
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          )
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === "left") {
            return (
              <ChevronLeftIcon className={cn("size-4", className)} {...props} />
            )
          }

          if (orientation === "right") {
            return (
              <ChevronRightIcon
                className={cn("size-4", className)}
                {...props}
              />
            )
          }

          return (
            <ChevronDownIcon className={cn("size-4", className)} {...props} />
          )
        },
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props} className="table-cell">
              <div className="flex items-center justify-center text-center">
                {children}
              </div>
            </td>
          )
        },
        ...components,
      }}
      {...props}
    />
  )
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const ref = React.useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        "flex items-center justify-center p-1 rounded-md text-xs font-medium hover:bg-gray-100",
        "data-[selected-single=true]:bg-blue-500 data-[selected-single=true]:text-white",
        className
      )}
      {...props}
    />
  )
}

export { Calendar, CalendarDayButton }
